import { LoadFailed, QueryLoading } from '../components/QueryState';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Button, Panel, PanelHeader } from '@promanage/ui';
import { nzDate } from '@promanage/core';
import { ReportWorkspace } from '../features/reports/ReportWorkspace';
import { LogFilters } from '../features/reports/LogFilters';
import { AuditRecordFilters, recordName } from '../features/reports/AuditRecordFilters';
import { AuditReport } from '../features/reports/AuditReport';
import { ExportPdfButton } from '../features/reports/ExportPdfButton';
import { describeAuditFilter, toAuditQuery } from '../features/reports/auditFilter';
// The route's own search parsing is its own module, and stays free of the event vocabulary —
// `validateSearch` runs during routing, so anything it imports is in the eager bundle.
import { parseAuditSearch, type AuditFilter } from '../features/reports/auditSearch';
import { letterheadLogo } from '../features/branding/letterhead';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/reports/audit')({
  // Shape-checked only — see `parseAuditSearch`. A malformed range is refused by the contract,
  // which is the boundary a pasted URL crosses anyway, and the server's sentence beside the
  // filter beats a router error page.
  validateSearch: parseAuditSearch,
  component: AuditReportPage,
});

/**
 * The system audit log.
 *
 * WHAT IT IS FOR. Every write path in this product records what it did, inside the transaction
 * that did it, into a hash-chained table the application cannot update or delete. That has been
 * true since `0003_auth`, and until now there was no way to read it back — the evidence existed
 * and nobody could produce it. This is the producing.
 *
 * IT IS A REPORT, NOT A LIST, and three things follow. The range is stated ON the page rather
 * than implied by the controls above it, so a filed printout still says what it covers. The total
 * says what it covers too, and admits when it is showing less than it found. And the whole thing
 * prints — see the `@media print` block in styles.css, which is most of the work, because the app
 * shell owns scrolling and a clipped overflow container prints exactly one screenful.
 *
 * THE FILTER LIVES IN THE URL, like the transaction list's. A report is something somebody
 * reloads, bookmarks, and sends to a colleague with "this is the bit I mean"; component state
 * survives none of the three. It also gives paging its reset for free — the filter is part of the
 * query key, so moving a date starts a fresh report rather than appending August to July.
 */
function AuditReportPage() {
  const filter = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const today = nzDate(new Date());

  const actors = useQuery(
    // Its own read: the people who appear in the log do not change as the range does, and folding
    // this into the report would empty the options as the filter is used.
    trpc.reports.auditActors.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load people for this filter' },
      staleTime: 5 * 60_000,
    }),
  );

  /*
   * The two lists behind the record filter, on the same terms as the actors above: they do not
   * change as the range or the categories do, so they are their own reads rather than fields on
   * the report — options that emptied themselves as the filter was used would be worse than none.
   *
   * THE SECOND ONE WAITS FOR THE FIRST TO BE ANSWERED BY THE OPERATOR. There is no useful list of
   * "every record of every kind": the kinds are read from the log, and only once one is chosen is
   * there a table to name its records from. `enabled` is what keeps a page load from asking.
   */
  const subjectTypes = useQuery(
    trpc.reports.auditSubjectTypes.queryOptions(undefined, {
      meta: { errorTitle: 'Could not load record types' },
      staleTime: 5 * 60_000,
    }),
  );

  const records = useQuery(
    trpc.reports.auditSubjects.queryOptions(
      // The empty string is never sent: the query is disabled until a type is chosen, and this is
      // the shape tRPC needs to build a key. Same arrangement as `JournalDetail`.
      { subjectType: filter.subjectType ?? '' },
      {
        meta: { errorTitle: 'Could not load records for this filter' },
        enabled: filter.subjectType !== undefined,
        staleTime: 5 * 60_000,
      },
    ),
  );

  /*
   * Whose agency this is, for the printed masthead.
   *
   * The same query the account menu runs, so on any session that has opened the menu this is a
   * cache hit. A report handed to an auditor with no agency name on it is a page of events about
   * nobody in particular — and a franchise operator with two memberships would have no way to
   * tell two printouts apart.
   */
  const orgs = useQuery(trpc.auth.organisations.queryOptions(undefined, { staleTime: 60_000 }));
  const orgName = orgs.data?.find((org) => org.isCurrent)?.name ?? null;

  /*
   * Keyset paging, by the chain's own sequence.
   *
   * `getNextPageParam` reads the server's answer rather than counting rows: the read fetches one
   * more than the limit and reports `nextCursor` from it, so "is there another page" is settled
   * by the same query that returned this one. Two queries could disagree while somebody is
   * working, and on a report whose whole value is completeness that disagreement is the bug.
   */
  const report = useInfiniteQuery(
    trpc.reports.audit.infiniteQueryOptions(toAuditQuery(filter), {
      meta: { errorTitle: 'We could not produce that report' },
      getNextPageParam: (page) => page.nextCursor,
      staleTime: 30_000,
    }),
  );

  const pages = report.data?.pages ?? [];
  const rows = pages.flatMap((page) => page.rows);
  // Counted once, on the first page — see `AuditReport.total`. Later pages carry null.
  const total = pages[0]?.total ?? null;

  const actor = actors.data?.find((entry) => entry.userId === filter.actor);
  const actorName = actor?.name ?? actor?.email ?? null;

  /*
   * The chosen record, NAMED, for the heading and for the printed page.
   *
   * Null when the list has not answered or does not carry it — the capped list can legitimately
   * not contain a record somebody linked to. `describeAuditFilter` says "one record" in that case
   * rather than falling silent, because a filed printout cannot be asked what it left out.
   */
  const record = records.data?.subjects.find((entry) => entry.id === filter.subjectId);
  const chosenRecord = record === undefined ? null : recordName(record);

  return (
    <ReportWorkspace
      title="System audit log"
      subtitle={describeAuditFilter(filter, actorName, chosenRecord)}
      actions={
        <>
          {/*
           * TWO WAYS OUT, and they are not redundant.
           *
           * PRINT costs nothing to offer: no download, the operator's own paper size, and
           * Save-as-PDF in the same dialogue. It is the right default and it stays the default.
           * What it cannot do is number its own pages — `@page` margin boxes are Paged Media
           * and no browser implements them — so a printed stack carries the browser's footer
           * or none at all.
           *
           * EXPORT builds the document with jspdf, which knows how many pages it made. That is
           * worth ~130 KB to somebody filing evidence, and nothing at all to everybody else,
           * which is exactly why the library is behind a click. See `ExportPdfButton`.
           */}
          <ExportPdfButton
            label="Download PDF"
            disabled={rows.length === 0}
            onExport={async () => {
              // The renderer, fetched by this click and never by the page view — the named
              // import means nothing else in the module is reachable from here.
              const { exportAuditPdf } = await import('../features/reports/auditPdf');
              exportAuditPdf({
                filter,
                actorName,
                recordName: chosenRecord,
                orgName,
                logo: await letterheadLogo(),
                rows,
                total,
                generatedAt: new Date(),
                hasMore: report.hasNextPage,
              });
            }}
          />
          <Button size="sm" onClick={() => window.print()}>
            Print
          </Button>
        </>
      }
      controls={
        <Panel className="print:hidden">
          <PanelHeader
            title="Report selection"
            subtitle="Narrow the report to the events you want to see. Every filter is optional, and the range is inclusive."
          />
          <LogFilters
            filter={filter}
            today={today}
            actors={actors.data ?? []}
            extra={
              <AuditRecordFilters
                filter={filter}
                subjectTypes={subjectTypes.data ?? []}
                records={records.data ?? null}
                recordsPending={records.isFetching}
                onChange={(next) => {
                  void navigate({ search: next, replace: true });
                }}
              />
            }
            onChange={(next: AuditFilter) => {
              // `replace` so the back button leaves the report rather than walking every filter
              // change somebody made on the way to the range they wanted.
              void navigate({ search: next, replace: true });
            }}
          />
        </Panel>
      }
    >
      <div className="flex flex-col gap-4">
        {subjectTypes.isError ? (
          <LoadFailed
            retry={() => void subjectTypes.refetch()}
            retrying={subjectTypes.isFetching}
          />
        ) : null}
        {records.isError ? (
          <LoadFailed retry={() => void records.refetch()} retrying={records.isFetching} />
        ) : null}
        {actors.isError ? (
          <LoadFailed retry={() => void actors.refetch()} retrying={actors.isFetching} />
        ) : null}
        {report.isError ? (
          <LoadFailed retry={() => void report.refetch()} retrying={report.isFetching} />
        ) : null}

        {report.isPending ? (
          <QueryLoading />
        ) : (
          <AuditReport
            filter={filter}
            actorName={actorName}
            recordName={chosenRecord}
            orgName={orgName}
            rows={rows}
            total={total}
            generatedAt={new Date()}
            isPending={report.isPending}
            hasMore={report.hasNextPage}
            isLoadingMore={report.isFetchingNextPage}
            onLoadMore={() => void report.fetchNextPage()}
          />
        )}
      </div>
    </ReportWorkspace>
  );
}
